import { faClose, faFingerprint } from "@fortawesome/free-solid-svg-icons"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import type { TimeClockEmployeeSummary } from "../../../Objects/TimeClock/TimeClockEmployeeSummary"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"

interface TimeClockPinPopupProps {
  employee: TimeClockEmployeeSummary
  onClose: () => void
  onSave: (pin: string) => Promise<boolean>
}

const pinFormat = /^\d{4,8}$/

export const TimeClockPinPopup: React.FC<TimeClockPinPopupProps> = ({ employee, onClose, onSave }) => {
  const { t } = useTranslation(["timeClock", "helpPopup"])

  const [pin, setPin] = useState("")

  const [confirmation, setConfirmation] = useState("")

  const [error, setLocalError] = useState("")

  const [isSaving, setIsSaving] = useState(false)

  const handleSubmit = async () => {
    if (isSaving) return

    if (!pinFormat.test(pin.trim())) {
      setLocalError(t("pin.format"))

      return
    }

    if (pin.trim() !== confirmation.trim()) {
      setLocalError(t("pin.mismatch"))

      return
    }

    setLocalError("")

    setIsSaving(true)

    try {
      await onSave(pin.trim())
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("pin.title", { name: employee.employeeName })}
              icon={faFingerprint}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.time-clock-pin"
                    title={t("helpPopup:global.timeClockPin.title")}
                    description={t("helpPopup:global.timeClockPin.description")}
                    bullets={t("helpPopup:global.timeClockPin.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockPinPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <form
              className="flex flex-col gap-3 p-2"
              onSubmit={(event) => {
                event.preventDefault()
                event.stopPropagation()
                void handleSubmit()
              }}
            >
              <Animate type={AnimationTypes.slideUpIn} duration="1.6s">
                <p className="text-xs text-slate-500">{t("pin.description")}</p>
              </Animate>

              <InputField
                type="password"
                value={pin}
                required
                inputMode="numeric"
                maxLength={8}
                icon={faFingerprint}
                placeholder={t("pin.field")}
                onChange={(event) => setPin(event.target.value)}
                descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockPinPopup.input.pin"
              />

              <InputField
                type="password"
                value={confirmation}
                required
                inputMode="numeric"
                maxLength={8}
                icon={faFingerprint}
                placeholder={t("pin.confirm_field")}
                errorMessage={error}
                onChange={(event) => setConfirmation(event.target.value)}
                descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockPinPopup.input.confirmation"
              />

              <div className={defaultStyle.divisionBar}></div>

              <Animate type={AnimationTypes.slideUpIn} duration="1.9s">
                <div className={defaultStyle.buttonsController}>
                  <Button
                    typeButton="submit"
                    label={t("pin.save")}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    disabled={isSaving}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockPinPopup.button.save"
                  />
                </div>
              </Animate>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
